// Le contrat du document ABS (« terobs.abs/1 »), recopié de `backend/app/etudes/abs/schema.py`.
//
// Ce fichier suit le contrat, il ne le définit pas : tout changement commence par `schema.py`, ses
// tests et le cahier des charges (docs/cahier-des-charges/2026-10-14-abs-sur-le-site.md, § 4), puis
// arrive ici. Règle de recopie : un champ sans valeur par défaut côté pydantic est obligatoire ici ;
// un champ qui en a une est facultatif (le serveur peut l'omettre), avec la valeur par défaut en
// commentaire. Les dates arrivent en chaînes ISO 8601.
//
// Les règles de contrôle (cellule vide sans motif, sources déclarées, chiffres modifiés :
// `verifier_modifications`) restent au serveur, qui les applique à chaque sauvegarde (422 avec la
// liste des erreurs). Le navigateur ne les réimplémente pas : il produit des modifications
// conformes (edition.ts) et affiche la réponse du serveur.

export const SCHEMA = 'terobs.abs/1'

/** D'où vient un contenu : un calcul sur la base, une phrase à gabarit remplie par le calcul, le
    modèle de langage (brouillon encadré), un humain de l'équipe, ou une donnée fournie par le CCAS
    et saisie par l'équipe. */
export type Origine = 'calcul' | 'gabarit' | 'ia' | 'humain' | 'ccas'
export type StatutTexte = 'brouillon' | 'valide'
export type StatutDocument = 'generation' | 'echec' | 'brouillon' | 'en_relecture' | 'valide' | 'livre'
export type RoleTexte = 'constats' | 'anticiper' | 'piste' | 'enjeux' | 'synthese' | 'methode' | 'libre'
export type NiveauAlerte = 'alerte' | 'vigilance' | 'piste'

/** L'ordre et les titres des parties (CDC § 3). */
export const SECTIONS: readonly (readonly [string, string])[] = [
  ['resume', 'Résumé pour les élus'],
  ['p1-contexte', 'Partie I — Contexte territorial et méthode'],
  ['p2-demographie', 'Partie II — Démographie et structure de la population'],
  ['p3-revenus', 'Partie III — Revenus, précarité et accès aux droits'],
  ['p4-logement', 'Partie IV — Habitat et logement'],
  ['p5-emploi', 'Partie V — Emploi et insertion professionnelle'],
  ['p6-seniors', 'Partie VI — Vieillissement, autonomie et seniors'],
  ['p7-familles', 'Partie VII — Familles, petite enfance et jeunesse'],
  ['p8-sante', 'Partie VIII — Accès aux soins et santé'],
  ['p9-geographie', 'Partie IX — Géographie sociale : IRIS et quartiers prioritaires'],
  ['p10-synthese', 'Partie X — Synthèse et orientations'],
  ['annexes', 'Annexes'],
]

/** Caractères, espaces exclus aux extrémités. */
export const JUSTIFICATION_MIN = 10

/** La trace d'un chiffre modifié par un humain : ce que le calcul donnait (valeur, texte, unité,
    source, millésime), pourquoi on l'a changé, qui, quand. Le client n'envoie que la justification :
    le serveur pose l'origine, l'auteur et la date (`tracer_modifications`). */
export interface Modification {
  valeur_origine?: number | null
  texte_origine?: string | null
  unite_origine?: string | null
  source_origine?: string | null
  millesime_origine?: string | null
  decimales_origine?: number | null
  justification: string
  auteur_id?: string | null
  auteur_nom?: string | null
  le?: string | null
}

export interface Cellule {
  valeur?: number | null
  /** contenu non numérique (« 4 quartiers », « Vallée Sud-Grand Paris ») ; un texte blanc vaut absence */
  texte?: string | null
  /** « % », « € », « hab. », « pt », « ‰ », « pour 10 000 hab. »… */
  unite?: string | null
  /** 0 à 3, défaut 0 */
  decimales?: number
  /** obligatoire quand la cellule n'a ni valeur ni texte */
  nd_motif?: string | null
  /** clé de Document.sources */
  source?: string | null
  /** « 2022 », « 2021 (déciles) », « 2025-09 » */
  millesime?: string | null
  /** défaut « calcul » */
  origine?: Origine
  modification?: Modification | null
}

export interface Referent {
  /** commune, epci, comparables, departement, region, france */
  cle: string
  libelle: string
  code?: string | null
  /** les communes comparables */
  codes?: string[]
  /** la règle publiée qui les a choisies */
  regle?: string | null
}

export interface Source {
  libelle: string
  producteur: string
  millesime?: string | null
  maille?: string | null
  url?: string | null
  /** date ISO (AAAA-MM-JJ) */
  publie_le?: string | null
}

/** Le contrôle automatique d'un texte rédigé par le modèle : chaque nombre du texte doit exister
    dans le document. */
export interface Verification {
  modele: string
  le: string
  nombres_verifies: number
  nombres_rejetes?: string[]
}

export interface ChiffreCle {
  id: string
  libelle: string
  cellule: Cellule
  /** « France métropolitaine » */
  comparaison_libelle?: string | null
  comparaison?: Cellule | null
}

// `type` a une valeur par défaut côté pydantic, mais c'est le discriminant de l'union des blocs :
// le serveur le sert toujours, et la sauvegarde le renvoie.
export interface BlocChiffresCles {
  type: 'chiffres_cles'
  id: string
  titre?: string | null
  items: ChiffreCle[]
}

/** Texte en markdown restreint : paragraphes, listes à puces, **gras**. Jamais de HTML. */
export interface BlocTexte {
  type: 'texte'
  id: string
  role: RoleTexte
  titre?: string | null
  /** défaut « » */
  contenu?: string
  /** ce que le bloc doit contenir (affiché à l'éditeur, jamais au client) */
  consigne?: string | null
  /** défaut « gabarit » */
  origine?: Origine
  /** défaut « brouillon » */
  statut?: StatutTexte
  verification?: Verification | null
}

export interface Colonne {
  cle: string
  libelle: string
}

export interface Ligne {
  id: string
  libelle: string
  cellules: Record<string, Cellule>
}

export interface BlocTableau {
  type: 'tableau'
  id: string
  titre: string
  colonnes: Colonne[]
  lignes: Ligne[]
  note?: string | null
}

export interface BlocAlerte {
  type: 'alerte'
  id: string
  niveau: NiveauAlerte
  contenu: string
  /** la règle publiée qui a déclenché l'alerte (alertes calculées) */
  regle?: string | null
  /** défaut « calcul » */
  origine?: Origine
  /** défaut « brouillon » */
  statut?: StatutTexte
  /** pour une piste rédigée par l'IA (un texte de l'IA porte toujours sa vérification) */
  verification?: Verification | null
}

/** Une donnée que seul le CCAS ou ses partenaires détiennent (capacité du SSIAD, aides
    facultatives…). Saisie par l'équipe : origine « ccas », source libre obligatoire. */
export interface BlocACompleter {
  type: 'a_completer'
  id: string
  libelle: string
  consigne: string
  cellule?: Cellule | null
  source_libre?: string | null
}

export type Bloc = BlocChiffresCles | BlocTexte | BlocTableau | BlocAlerte | BlocACompleter

export interface Section {
  id: string
  titre: string
  sous_titre?: string | null
  /** défaut false */
  masquee?: boolean
  /** défaut [] */
  blocs?: Bloc[]
}

export interface Document {
  /** `schema_` côté pydantic, sérialisé sous son alias « schema » ; défaut « terobs.abs/1 » */
  schema?: typeof SCHEMA
  insee: string
  commune: string
  genere_le: string
  /** date ISO (AAAA-MM-JJ) */
  donnees_au?: string | null
  referents: Referent[]
  sources: Record<string, Source>
  sections: Section[]
}
